import { useMemo } from 'react';

const toPositiveNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 0;
  }

  return numeric;
};

const formatPx = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return undefined;
  }

  const rounded = Math.round(value * 100) / 100;
  return `${rounded}px`;
};

const computeFrameExtras = (frame, pxPerCm) => {
  if (!frame || !Number.isFinite(pxPerCm) || pxPerCm <= 0) {
    return {
      frameWidthPx: 0,
      matWidthPx: 0,
      totalExtraPx: 0,
    };
  }

  const frameWidthCm = toPositiveNumber(frame.frameWidthCm);
  const matWidthCm = toPositiveNumber(frame.matWidthCm);
  const frameWidthPx = frameWidthCm > 0 ? frameWidthCm * pxPerCm : 0;
  const matWidthPx = matWidthCm > 0 ? matWidthCm * pxPerCm : 0;
  const totalExtraPx = (frameWidthPx + matWidthPx) * 2;

  return {
    frameWidthPx,
    matWidthPx,
    totalExtraPx,
  };
};

export const getFrameExtraDimensions = (frame, pxPerCm) => {
  const { totalExtraPx } = computeFrameExtras(frame, pxPerCm);
  return {
    extraWidthPx: totalExtraPx,
    extraHeightPx: totalExtraPx,
  };
};

export default function useFrameMeasurements({
  frame,
  pxPerCm,
  artworkWidthPx,
  artworkHeightPx,
}) {
  return useMemo(() => {
    const baseWidth = Number.isFinite(artworkWidthPx) && artworkWidthPx > 0 ? artworkWidthPx : 0;
    const baseHeight = Number.isFinite(artworkHeightPx) && artworkHeightPx > 0 ? artworkHeightPx : 0;

    if (!frame || !Number.isFinite(pxPerCm) || pxPerCm <= 0) {
      return {
        frameWidthPx: 0,
        matWidthPx: 0,
        renderedWidthPx: baseWidth,
        renderedHeightPx: baseHeight,
        frameInlineStyles: undefined,
        matInlineStyles: undefined,
      };
    }

    const {
      frameWidthPx,
      matWidthPx,
      totalExtraPx,
    } = computeFrameExtras(frame, pxPerCm);
    const totalExtraWidth = totalExtraPx;
    const totalExtraHeight = totalExtraPx;
    const frameContentWidth = baseWidth + (matWidthPx * 2);
    const frameInlineStyles = {
      width: formatPx(frameContentWidth > 0 ? frameContentWidth : baseWidth),
      /* height: auto (omit) so the frame wraps the image’s natural height */
      borderWidth: formatPx(frameWidthPx),
      borderStyle: frameWidthPx > 0 ? 'solid' : undefined,
      '--frame-rim-width': formatPx(frameWidthPx),
    };

    const matInlineStyles = matWidthPx > 0
      ? {
        padding: formatPx(matWidthPx),
        backgroundColor: '#fff',
      }
      : undefined;

    return {
      frameWidthPx,
      matWidthPx,
      renderedWidthPx: baseWidth + totalExtraWidth,
      // height will be auto from the image; keep this value but it’s no longer used for layout
      renderedHeightPx: baseHeight + totalExtraHeight,
      frameInlineStyles,
      matInlineStyles,
    };
  }, [artworkHeightPx, artworkWidthPx, frame, pxPerCm]);
}
